import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Clock, Check, X, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import notificationService from '../../services/notificationService';

const NotificationSettings = ({ onClose }) => {
  const [isSupported, setIsSupported] = useState(true);
  const [permission, setPermission] = useState('default');
  const [enabled, setEnabled] = useState(false);
  const [reminderHour, setReminderHour] = useState(19);
  const [reminderMinute, setReminderMinute] = useState(0);
  const [showTestSuccess, setShowTestSuccess] = useState(false);

  useEffect(() => {
    // Check support and load settings
    setIsSupported(notificationService.checkSupport());
    setPermission(notificationService.getPermissionStatus());
    
    const settings = notificationService.loadSettings();
    if (settings) {
      setEnabled(settings.enabled || false);
      setReminderHour(settings.hour || 19);
      setReminderMinute(settings.minute || 0);
    }
  }, []);

  const handleRequestPermission = async () => {
    const result = await notificationService.requestPermission();
    setPermission(result);
    if (result === 'granted') {
      setEnabled(true);
      notificationService.scheduleDailyReminder(reminderHour, reminderMinute);
    }
  };

  const handleToggleNotifications = (checked) => {
    setEnabled(checked);
    if (checked && permission === 'granted') {
      notificationService.scheduleDailyReminder(reminderHour, reminderMinute);
    } else {
      notificationService.disable();
    }
  };

  const handleTimeChange = (hour, minute) => {
    setReminderHour(hour);
    setReminderMinute(minute);
    if (enabled && permission === 'granted') {
      notificationService.scheduleDailyReminder(hour, minute);
    }
  };

  const handleTestNotification = () => {
    notificationService.sendNotification('🎉 Teste de Notificação!', {
      body: 'As notificações estão funcionando perfeitamente!',
      tag: 'test'
    });
    setShowTestSuccess(true);
    setTimeout(() => setShowTestSuccess(false), 3000);
  };

  // Generate time options
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];

  if (!isSupported) {
    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center gap-3 text-warning">
          <BellOff className="w-6 h-6" />
          <div>
            <p className="font-semibold">Notificações não suportadas</p>
            <p className="text-sm text-muted-foreground">
              Seu navegador não suporta notificações push.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Bell className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold">Lembretes de Estudo</h3>
            <p className="text-sm text-muted-foreground">
              Receba notificações para manter o ritmo
            </p>
          </div>
        </div>
        {onClose && (
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Permission Status */}
      {permission !== 'granted' && (
        <div className={cn(
          'p-4 rounded-xl',
          permission === 'denied' ? 'bg-destructive/10' : 'bg-primary/10'
        )}>
          {permission === 'denied' ? (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <BellOff className="w-5 h-5 text-destructive mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">Notificações bloqueadas</p>
                  <p className="text-sm text-muted-foreground">
                    Você bloqueou as notificações. Para ativá-las, acesse as configurações do seu navegador.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="font-medium">Ative as notificações</p>
                  <p className="text-sm text-muted-foreground">
                    Receba lembretes diários para não perder seu streak de estudos!
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleRequestPermission}
                className="w-full btn-primary-gradient"
              >
                <Bell className="w-4 h-4 mr-2" />
                Permitir Notificações
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Settings (only show if permission granted) */}
      {permission === 'granted' && (
        <>
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
            <div className="flex items-center gap-3">
              <Bell className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium">Lembretes Diários</p>
                <p className="text-xs text-muted-foreground">
                  {enabled ? 'Ativo' : 'Desativado'}
                </p>
              </div>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggleNotifications}
            />
          </div>

          {/* Time Selection */}
          {enabled && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Horário do Lembrete
              </div>
              <div className="flex gap-3">
                <select
                  value={reminderHour}
                  onChange={(e) => handleTimeChange(parseInt(e.target.value), reminderMinute)}
                  className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {hours.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour.toString().padStart(2, '0')}h
                    </option>
                  ))}
                </select>
                <select
                  value={reminderMinute}
                  onChange={(e) => handleTimeChange(reminderHour, parseInt(e.target.value))}
                  className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {minutes.map((minute) => (
                    <option key={minute} value={minute}>
                      {minute.toString().padStart(2, '0')}min
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-muted-foreground">
                Você receberá um lembrete todos os dias às {reminderHour.toString().padStart(2, '0')}:{reminderMinute.toString().padStart(2, '0')}
              </p>
            </div>
          )}

          {/* Test Notification */}
          {enabled && (
            <div className="pt-2">
              <Button
                variant="outline"
                onClick={handleTestNotification}
                className="w-full"
                disabled={showTestSuccess}
              >
                {showTestSuccess ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-success" />
                    Notificação Enviada!
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-2" />
                    Testar Notificação
                  </>
                )}
              </Button>
            </div>
          )}
        </>
      )}

      {/* Info */}
      <div className="p-4 rounded-xl bg-muted/30 border border-border">
        <p className="text-xs text-muted-foreground text-center">
          💡 Dica: Estudar no mesmo horário todo dia ajuda a criar um hábito e manter seu streak!
        </p>
      </div>
    </div>
  );
};

export default NotificationSettings;
